import api from '../../../services/api';
import { toAnalyticsParams } from '../../../components/performance/pmAnalyticsFilters';
import { PanelError } from '../../performance-operations/config';

// PM analitik P5 — E12 "Gelişim Planları ve PIP" veri katmanı (analiz §15, §24 M33–M38). Uçlar:
//   GET    v1/talent-analytics/development/summary            M33, M34, M35; durum × birim; aksiyon son tarih takvimi; dikkat
//   GET    v1/talent-analytics/development/plans               plan listesi (sayfalı; durum, gelişim alanı, geciken, plan gerekli)
//   GET    v1/talent-analytics/development/requirements        "plan gerekli" işaretleri (M33 paydası)
//   POST   v1/talent-analytics/development/requirements        işaretle {periodId, employeeIds, reason}
//   DELETE v1/talent-analytics/development/requirements/{id}   işareti kaldır
//   POST   v1/talent-analytics/development/requirements/apply-rule   İK: düşük bant kuralı {periodId, rule: 'low_band'}
//   GET    v1/talent-analytics/pip/summary | pip/list          yalnız İK — M36, M37, M38; durumlar; kapanış penceresi
// api.js GET'te reject ETMEZ (sinyal res.data.status); yazmalarda hata tostu interceptor'da.

export const IDP_STATUSES = ['draft', 'meeting', 'manager_approval', 'revision_required', 'active', 'period_end_review', 'completed', 'cancelled'];
export const IDP_CATEGORIES = ['technical', 'leadership', 'personal'];
export const PIP_STATUSES = ['draft', 'active', 'completed_successful', 'completed_unsuccessful', 'cancelled'];
export const REQUIREMENT_PAGE = 8;

export const TAB_IDP = 'idp';
export const TAB_PIP = 'pip';
export const Q_TAB = 'dtab';

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

async function get(url, params) {
  const res = await api.get(url, { params });
  const data = payload(res);
  if (data === undefined) throw new PanelError(res?.data?.message || 'error');
  return data;
}

const pageRows = (data) => (Array.isArray(data) ? data : (data?.data || []));

export const fetchDevelopmentSummary = (filters, categories) => get('/v1/talent-analytics/development/summary', {
  ...toAnalyticsParams(filters), ...(categories?.length ? { 'category[in]': categories.join(',') } : {}),
});

/** Plan listesi süzgeçleri → uç parametreleri. */
export function planParams(planFilters) {
  const f = planFilters || {};
  return {
    ...(f.statuses?.length ? { 'status[in]': f.statuses.join(',') } : {}),
    ...(f.categories?.length ? { 'category[in]': f.categories.join(',') } : {}),
    ...(f.overdue ? { overdue: 'true' } : {}),
    ...(f.required ? { required: 'true' } : {}),
  };
}

export const fetchPlans = async (filters, planFilters, pageNumber, pageSize) => {
  const data = await get('/v1/talent-analytics/development/plans', {
    ...toAnalyticsParams(filters), ...planParams(planFilters), pageNumber, pageSize,
  });
  return { rows: pageRows(data), total: data?.totalRowCount ?? pageRows(data).length };
};

export const fetchRequirements = async (filters, pageNumber) => {
  const data = await get('/v1/talent-analytics/development/requirements', {
    ...toAnalyticsParams(filters), pageNumber, pageSize: REQUIREMENT_PAGE,
  });
  return { rows: pageRows(data), total: data?.totalRowCount ?? pageRows(data).length };
};

export async function addRequirements(periodId, employeeIds, reason) {
  try {
    const res = await api.post('/v1/talent-analytics/development/requirements', { periodId, employeeIds, reason: reason || null });
    return res?.data?.data || { added: 0 };
  } catch {
    return null;
  }
}

export async function removeRequirement(id) {
  try {
    await api.delete(`/v1/talent-analytics/development/requirements/${id}`);
    return true;
  } catch {
    return false;
  }
}

export async function applyLowBandRule(periodId) {
  try {
    const res = await api.post('/v1/talent-analytics/development/requirements/apply-rule', { periodId, rule: 'low_band' });
    return res?.data?.data || { added: 0 };
  } catch {
    return null;
  }
}

/** Kapanış penceresi (yalnız tarih; kiracı gününe göre sunucuda yorumlanır). */
export const windowParams = (closeWindow) => ({
  ...(closeWindow?.from ? { 'closedAt[gte]': closeWindow.from } : {}),
  ...(closeWindow?.to ? { 'closedAt[lte]': closeWindow.to } : {}),
});

export const fetchPipSummary = (filters, closeWindow) => get('/v1/talent-analytics/pip/summary', {
  ...toAnalyticsParams(filters), ...windowParams(closeWindow),
});

export const fetchPips = async (filters, closeWindow, statuses, pageNumber, pageSize) => {
  const data = await get('/v1/talent-analytics/pip/list', {
    ...toAnalyticsParams(filters), ...windowParams(closeWindow), ...(statuses?.length ? { 'status[in]': statuses.join(',') } : {}),
    pageNumber, pageSize,
  });
  return { rows: pageRows(data), total: data?.totalRowCount ?? pageRows(data).length };
};

/** Durum × birim → yığılmış sütun satırları (birim etiketi; "other" = ilk N dışı; atanmamış). */
export function unitChartRows(t, units) {
  return (units || []).map((u) => {
    let label = u.label;
    if (u.key === 'other') label = t('performance.developmentAnalytics.unit.other');
    else if (!u.key) label = t('performance.developmentAnalytics.unit.unassigned');
    return { key: u.key || 'unassigned', label: label || '-', ...IDP_STATUSES.reduce((acc, s) => ({ ...acc, [s]: u.counts?.[s] || 0 }), {}), total: u.total };
  });
}

/** Takvim çubukları: gecikmiş, aylar, sonrası, tarihsiz (tam bölüntü). */
export function calendarRows(t, calendar, lang) {
  if (!calendar) return [];
  const monthName = (m) => {
    const [y, mo] = m.split('-').map(Number);
    return new Intl.DateTimeFormat(lang, { month: 'short', year: 'numeric' }).format(new Date(Date.UTC(y, mo - 1, 15)));
  };
  return [
    { key: 'overdue', label: t('performance.developmentAnalytics.calendar.overdue'), count: calendar.overdue || 0 },
    ...(calendar.months || []).map((m) => ({ key: m.month, label: monthName(m.month), count: m.count || 0 })),
    { key: 'later', label: t('performance.developmentAnalytics.calendar.later'), count: calendar.later || 0 },
    { key: 'noDate', label: t('performance.developmentAnalytics.calendar.noDate'), count: calendar.noDate || 0 },
  ];
}

// ---------------------------------------------------------------- "plan gerekli" kişi seçici
const listOf = (res) => { const r = res?.data?.data; return Array.isArray(r) ? r : (r?.data || []); };
const fullName = (e) => `${e.firstName || ''} ${e.lastName || ''}`.trim();

/**
 * Kişi seçici kaynağı: İK çalışan aramasını (EM) kullanır; yönetici yalnız dönemin kişi kümesinden (kendi ekibi) seçer —
 * /v1/performance-analytics/participants aktörün kapsamını sunucuda uygular, ad araması istemcide (ekip küçüktür).
 */
export function employeeOptions(filters, isHr) {
  if (isHr) {
    return {
      onSearch: async (q) => {
        const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
        return listOf(res).map((e) => ({ id: e.id, name: fullName(e) }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/employees/${id}`);
        return { id, name: fullName(res?.data?.data || {}) };
      },
    };
  }
  let cache = null;
  const load = async () => {
    if (cache) return cache;
    const res = await api.get('/v1/performance-analytics/participants', { params: { ...toAnalyticsParams(filters), pageNumber: 1, pageSize: 200 } });
    cache = listOf(res).map((p) => ({ id: p.employeeId, name: p.fullName }));
    return cache;
  };
  return {
    onSearch: async (q) => {
      const all = await load();
      const needle = (q || '').toLocaleLowerCase();
      return needle ? all.filter((o) => o.name.toLocaleLowerCase().includes(needle)) : all.slice(0, 50);
    },
    onGetById: async (id) => (await load()).find((o) => o.id === id) || { id, name: '' },
  };
}
